/**
 * CommentsSection Component
 * Displays paginated comments for a power with add-comment form.
 * Used inside the Comments tab of PowerDetailModal.
 */

import { useState, useEffect } from 'react';
import {
  ChatBubbleLeftRightIcon,
  PlusIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { OathSpinner, OathButton, OathAlert, OathEmptyState } from '../../../shared/OathUI';
import {
  getPowerComments,
  createComment,
  COMMENT_MAX_LENGTH,
  COMMENT_WARNING_THRESHOLD,
  DEFAULT_COMMENTS_PAGE_SIZE,
} from '../../../../services/oathPowerCommentService';

// ============================================
// ENTRY TYPE STYLING
// ============================================

const ENTRY_TYPE_STYLES = {
  AGT: {
    label: 'Agent',
    classes: 'bg-primary-100 dark:bg-primary-900/30 text-primary dark:text-primary-light',
    bg: '',
  },
  SYS: {
    label: 'System',
    classes: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
    bg: 'bg-gray-50 dark:bg-gray-700/50',
  },
  USR: {
    label: 'Internal',
    classes: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    bg: '',
  },
};

// ============================================
// DATE FORMATTING
// ============================================

const formatCommentDate = (dateString) => {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

// ============================================
// COMMENT CARD
// ============================================

const CommentCard = ({ comment }) => {
  const style = ENTRY_TYPE_STYLES[comment.entryType] || ENTRY_TYPE_STYLES.AGT;

  return (
    <div className={`border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 ${style.bg}`}>
      {/* Header: badge + author + date */}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full flex-shrink-0 ${style.classes}`}>
            {style.label}
          </span>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
            {comment.recordedBy}
          </span>
        </div>
        <span className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 whitespace-nowrap">
          {formatCommentDate(comment.dateRecorded)}
        </span>
      </div>

      {/* Optional subject */}
      {comment.subject && (
        <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 mb-1">
          {comment.subject}
        </p>
      )}

      {/* Comment body */}
      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
        {comment.comment}
      </p>
    </div>
  );
};

// ============================================
// ADD COMMENT FORM
// ============================================

const AddCommentForm = ({ powerNo, onCommentAdded, onCancel }) => {
  const [commentText, setCommentText] = useState('');
  const [contactAgent, setContactAgent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const charCount = commentText.length;
  const showCounter = charCount > COMMENT_WARNING_THRESHOLD;
  const isNearLimit = charCount > 3900;
  const isAtLimit = charCount >= COMMENT_MAX_LENGTH;
  const canSubmit = commentText.trim().length > 0 && !isAtLimit && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    setSubmitError(null);

    const result = await createComment({
      powerNumber: powerNo,
      comment: commentText.trim(),
      contactAgent,
    });

    if (result.success) {
      toast.success('Comment added');
      onCommentAdded(result.data);
      setCommentText('');
      setContactAgent(false);
    } else {
      setSubmitError(result.error || 'Failed to add comment');
    }

    setIsSubmitting(false);
  };

  return (
    <div className="border border-primary/20 dark:border-primary/30 rounded-xl p-3 sm:p-4 bg-primary-50/30 dark:bg-primary-900/10 space-y-3">
      {/* Textarea */}
      <div>
        <textarea
          value={commentText}
          onChange={(e) => {
            if (e.target.value.length <= COMMENT_MAX_LENGTH) {
              setCommentText(e.target.value);
            }
          }}
          placeholder="Write a comment..."
          rows={3}
          className="w-full px-4 py-3 border rounded-xl text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 border-gray-300 dark:border-gray-600 resize-none"
          disabled={isSubmitting}
        />
        {showCounter && (
          <div className="flex justify-end mt-1">
            <span className={`text-xs ${isNearLimit ? 'text-red-500 font-semibold' : 'text-gray-400 dark:text-gray-500'}`}>
              {charCount.toLocaleString()} / {COMMENT_MAX_LENGTH.toLocaleString()}
            </span>
          </div>
        )}
      </div>

      {/* Contact agent checkbox */}
      <label className="flex items-start gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={contactAgent}
          onChange={(e) => setContactAgent(e.target.checked)}
          disabled={isSubmitting}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary/30"
        />
        <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
          Contact agent <span className="text-gray-400 dark:text-gray-500">(notifies Bankers Surety via email)</span>
        </span>
      </label>

      {contactAgent && (
        <OathAlert variant="info" message="An email notification will be sent to Bankers Surety about this comment." />
      )}

      {/* Submit error */}
      {submitError && (
        <OathAlert variant="error" message={submitError} />
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <OathButton variant="outline" size="sm" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </OathButton>
        <OathButton variant="primary" size="sm" onClick={handleSubmit} loading={isSubmitting} disabled={!canSubmit}>
          Add Comment
        </OathButton>
      </div>
    </div>
  );
};

// ============================================
// COMMENTS SECTION (MAIN EXPORT)
// ============================================

const CommentsSection = ({ powerNo }) => {
  // Comment list state
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Form state
  const [isFormOpen, setIsFormOpen] = useState(false);

  // Initial fetch
  useEffect(() => {
    let cancelled = false;

    const fetchComments = async () => {
      if (!powerNo) return;

      setIsLoading(true);
      setError(null);
      setComments([]);
      setPage(1);

      const result = await getPowerComments(powerNo, 1, DEFAULT_COMMENTS_PAGE_SIZE);

      if (cancelled) return;

      if (result.success) {
        setComments(result.data.items);
        setTotalCount(result.data.totalCount);
        setTotalPages(result.data.totalPages);
      } else {
        setError(result.error || 'Failed to load comments');
      }

      setIsLoading(false);
    };

    fetchComments();
    return () => { cancelled = true; };
  }, [powerNo]);

  // Load more handler
  const handleLoadMore = async () => {
    const nextPage = page + 1;
    setIsLoadingMore(true);

    const result = await getPowerComments(powerNo, nextPage, DEFAULT_COMMENTS_PAGE_SIZE);

    if (result.success) {
      setComments(prev => [...prev, ...result.data.items]);
      setPage(nextPage);
      setTotalPages(result.data.totalPages);
    }

    setIsLoadingMore(false);
  };

  // After adding a comment, prepend to list
  const handleCommentAdded = (newComment) => {
    setComments(prev => [newComment, ...prev]);
    setTotalCount(prev => prev + 1);
    setIsFormOpen(false);
  };

  // Retry fetch
  const handleRetry = async () => {
    setIsLoading(true);
    setError(null);

    const result = await getPowerComments(powerNo, 1, DEFAULT_COMMENTS_PAGE_SIZE);

    if (result.success) {
      setComments(result.data.items);
      setTotalCount(result.data.totalCount);
      setTotalPages(result.data.totalPages);
      setPage(1);
    } else {
      setError(result.error || 'Failed to load comments');
    }

    setIsLoading(false);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <OathSpinner size="md" />
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading comments...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <ExclamationTriangleIcon className="h-10 w-10 text-red-400" />
        <p className="mt-3 text-sm text-red-500 dark:text-red-400">{error}</p>
        <button
          onClick={handleRetry}
          className="mt-4 px-4 py-2 text-sm text-primary dark:text-primary-light border border-primary/30 rounded-lg hover:bg-primary/10 transition-colors"
        >
          Try again
        </button>
      </div>
    );
  }

  // Empty state
  if (comments.length === 0 && !isFormOpen) {
    return (
      <OathEmptyState
        icon={ChatBubbleLeftRightIcon}
        title="No comments yet"
        message="Be the first to add a comment on this power."
        action={
          <OathButton variant="primary" size="sm" icon={PlusIcon} onClick={() => setIsFormOpen(true)}>
            Add Comment
          </OathButton>
        }
      />
    );
  }

  return (
    <div className="space-y-3">
      {/* Header row */}
      <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary dark:text-primary-light" />
          <h4 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
            Comments
            {totalCount > 0 && (
              <span className="ml-1.5 text-xs font-normal text-gray-400 dark:text-gray-500">
                ({totalCount})
              </span>
            )}
          </h4>
        </div>
        {!isFormOpen && (
          <button
            onClick={() => setIsFormOpen(true)}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs sm:text-sm font-medium text-primary dark:text-primary-light hover:bg-primary/10 dark:hover:bg-primary/20 rounded-lg transition-colors"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Add Comment
          </button>
        )}
      </div>

      {/* Add comment form (collapsible) */}
      {isFormOpen && (
        <AddCommentForm
          powerNo={powerNo}
          onCommentAdded={handleCommentAdded}
          onCancel={() => setIsFormOpen(false)}
        />
      )}

      {/* Comments list */}
      <div className="max-h-80 overflow-y-auto space-y-2 pr-1 -mr-1">
        {comments.map(comment => (
          <CommentCard key={comment.commentId} comment={comment} />
        ))}
      </div>

      {/* Load more */}
      {page < totalPages && (
        <div className="text-center pt-2">
          <button
            onClick={handleLoadMore}
            disabled={isLoadingMore}
            className="text-sm text-primary dark:text-primary-light hover:underline disabled:opacity-50"
          >
            {isLoadingMore ? 'Loading...' : `Load more (${comments.length} of ${totalCount})`}
          </button>
        </div>
      )}
    </div>
  );
};

export default CommentsSection;
